import { ViteSSG } from 'vite-ssg'
import App from './App.vue'
import { routes, scrollBehavior, SUBSCRIPTION_RETURN_KEY } from './router'
import { swapLocaleInPath, staticPageHref } from './router/products'
import { hasAuthToken } from './services/authSession'
import {
  createI18nInstance,
  DEFAULT_LOCALE,
  getPreferredLocale,
  setStoredLocale,
  type Locale,
} from './i18n'
import { initializeSentry } from './services/sentry'
import './styles/main.css'

export const createApp = ViteSSG(
  App,
  { routes, scrollBehavior },
  ({ app, router, isClient }) => {
    const i18n = createI18nInstance(DEFAULT_LOCALE)
    app.use(i18n)

    if (isClient) {
      initializeSentry(app, router)
    }

    router.beforeEach((to) => {
      const locale = (to.meta?.locale as Locale | undefined) ?? DEFAULT_LOCALE

      // Préférence de langue collante : une fois la langue choisie (ou chargée),
      // un retour navigateur vers une URL d'une autre langue est ré-aligné sur la
      // préférence — sinon le « Précédent » ramènerait à la version FR d'une étape.
      // On ne redirige que côté client (le SSG rend toujours la langue de l'URL)
      // et seulement si une équivalence existe (sinon : pas de boucle).
      if (isClient && to.meta?.locale) {
        const preferred = getPreferredLocale()
        if (preferred && preferred !== to.meta.locale) {
          const target = swapLocaleInPath(to.path, preferred)
          // Ré-alignement EN PLACE (replace, pas push). Un « Précédent » navigateur
          // qui franchit une frontière de langue — ex. après une bascule de langue
          // en cours de parcours, dont les entrées d'historique restent dans la
          // langue précédente — déclenche ce guard sur un popstate. Or vue-router
          // redirige un popstate via un push inconditionnel (sans replace) : le
          // clone ré-aligné se réinsère juste au-dessus de l'entrée dépilée, si
          // bien que le « Précédent » suivant y retombe → l'utilisateur reste
          // piégé à un cran du début. `replace: true` écrase cette entrée sur
          // place, laissant le « Précédent » redescendre tout le parcours jusqu'à
          // son point d'entrée.
          if (target !== to.path) return { path: target, hash: to.hash, replace: true }
        }
      }

      // Accueil réservé aux visiteurs : un adhérent connecté qui atteint la page
      // d'accueil est redirigé vers son espace personnel. Côté client uniquement
      // (le SSG rend toujours la home publique ; le token de session n'existe que
      // dans le navigateur, cf. authSession SSR-safe).
      if (isClient && (to.name === 'home-fr' || to.name === 'home-en') && hasAuthToken()) {
        return staticPageHref('account', locale)
      }

      i18n.global.locale.value = locale
      if (isClient) {
        document.documentElement.lang = locale
        // Mémorise la langue + le parcours en cours pour les retours externes
        // (Stripe → /subscription/end et /subscription/summary).
        setStoredLocale(locale)
        try {
          if (to.meta?.productKey) localStorage.setItem(SUBSCRIPTION_RETURN_KEY, to.fullPath)
        } catch {
          // localStorage indisponible (navigation privée) : on ignore.
        }
      }
    })
  },
)
